import { ReactNode } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "app/components/ui/tabs/Tabs";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import styles from "./AuthTabs.module.scss";
import LoginContainer from "app/pages/Auth/Signin/LoginContainer";
import SignupContainer from "app/pages/Auth/Signup/SignupContainer";
import { useLocation, useNavigate } from "react-router-dom";

type AuthTabsProps = {
	onValueChange?: (value: string) => void;
	onAuthSuccess?: () => void;
	className?: string;
	signupFooter?: ReactNode;
	signinFooter?: ReactNode;
	defaultValue?: "signup" | "login";
	value?: "signup" | "login";
	navigateOnChange?: boolean;
};

type AuthContainerProps = {
	onAuthSuccess?: (...args: unknown[]) => void;
	footer?: ReactNode;
};

const SignupContainerTyped = SignupContainer as React.ComponentType<AuthContainerProps>;
const LoginContainerTyped = LoginContainer as React.ComponentType<AuthContainerProps>;

export const AuthTabs = ({
	onValueChange,
	onAuthSuccess,
	className,
	signupFooter,
	signinFooter,
	defaultValue = "signup",
	value,
	navigateOnChange = false,
}: AuthTabsProps) => {
	const navigate = useNavigate();
	const location = useLocation();

	const handleValueChange = (value: string) => {
		if (navigateOnChange) {
			navigate(`${location.pathname.replace(/\/(login|signup)$/, "")}/${value}`);
		}

		if (typeof onValueChange === "function") {
			onValueChange(value);
		}
	};

	const tabsValueProps = typeof value === "string" ? { value } : { defaultValue };

	return (
		<Tabs
			{...tabsValueProps}
			className={classNames(styles["auth-tabs"], className)}
			onValueChange={handleValueChange}
		>
			<TabsList className={styles["auth-tabs__list"]}>
				<TabsTrigger value="signup" data-testid="switch-to-signUp">
					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} component="span">
						<FormattedMessage id="home.signup.button.label" />
					</Typography>
				</TabsTrigger>

				<TabsTrigger value="login" data-testid="switch-to-login">
					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} component="span">
						<FormattedMessage id="home.signin.button.label" />
					</Typography>
				</TabsTrigger>
			</TabsList>

			<TabsContent value="signup" className={styles["auth-tabs__content"]}>
				<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} component="span">
					<SignupContainerTyped onAuthSuccess={onAuthSuccess} footer={signupFooter} />
				</Typography>
			</TabsContent>

			<TabsContent value="login" className={styles["auth-tabs__content"]}>
				<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} component="span">
					<LoginContainerTyped onAuthSuccess={onAuthSuccess} footer={signinFooter} />
				</Typography>
			</TabsContent>
		</Tabs>
	);
};
